import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
  IconButton,
} from "@mui/material";
import {
  stableSort,
  getComparator,
} from "../../../../../common-utilities/sortingUtils";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmation from "../../../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";
import { DeleteTestSlate } from "../../../../services/TestLibService";

const SlatesTable = ({ slatesData, count, onChange }) => {
  const columns = [
    "Job ID",
    "Job Name",
    "Date Created",
    "List Price",
    "Adjusted Price",
    "Active",
    "Actions",
  ];
  const columnHeaders = [
    "jobId",
    "jobName",
    "dateCreated",
    "listPrice",
    "adjustedPrice",
    "active",
  ];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [selectedSlateToDelete, setSelectedSlateToDelete] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);

  useEffect(() => {
    handleChange();
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChange = () => onChange(page, rowsPerPage);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const openEditTab = (jobId) => {
    window.open(
      "/vlp/maintenance-test-slate/" + encodeURIComponent(jobId.trim()),
      "_blank"
    );
  };

  const handleDeleteClick = (slate) => {
    setSelectedSlateToDelete(slate.jobId);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => setIsDeleteConfirmationOpen(false);

  const handleDeleteConfirmationConfirm = async () => {
    try {
      const response = await DeleteTestSlate(selectedSlateToDelete);
      if (response.results === true) {
        NotificationManager.success("Test slate deleted.");
        setRowsPerPage(rowsPerPage + 1);
        setIsDeleteConfirmationOpen(false);
      } else {
        NotificationManager.error("Error deleting test slate.");
      }
    } catch (err) {
      NotificationManager.error("Error deleting test slate. " + err, "Error");
    }
  };

  const renderCell = (column, slate) => {
    if (column === "dateCreated")
      return new Date(slate[column]).toLocaleDateString("en-US");
    if (column === "active")
      return slate[column] ? "Active" : "Inactive";
    return slate[column];
  };

  const renderActions = (slate) => (
    <div style={{ minWidth: "100px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => openEditTab(slate.jobId)}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => openEditTab(slate.jobId)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(slate)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  return (
    <Paper style={{ marginRight: "20px" }}>
      <TableContainer>
        <Table sx={{ minWidth: 400 }} size="small" dense>
          <TableHead
            sx={{
              backgroundColor: (theme) => theme.palette.primary.main,
              color: "white",
            }}
          >
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={column}
                  onClick={() =>
                    columnHeaders[index] && handleRequestSort(columnHeaders[index])
                  }
                  sx={{ cursor: columnHeaders[index] ? "pointer" : "default" }}
                >
                  {columnHeaders[index] ? (
                    <TableSortLabel
                      active={orderBy === columnHeaders[index]}
                      direction={orderBy === columnHeaders[index] ? order : "asc"}
                      sx={{
                        color: "white !important",
                        "& .MuiTableSortLabel-icon": { color: "white !important" },
                      }}
                    >
                      {column}
                    </TableSortLabel>
                  ) : (
                    column
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {stableSort(slatesData, getComparator(order, orderBy)).map((slate, index) => (
              <TableRow
                key={slate.jobId}
                sx={{
                  backgroundColor:
                    index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                }}
              >
                {columnHeaders.map((column) => (
                  <TableCell key={column}>{renderCell(column, slate)}</TableCell>
                ))}
                <TableCell>{renderActions(slate)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={count}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message="Are you sure you want to delete this slate?"
        color="red"
      />
    </Paper>
  );
};

export default SlatesTable;
